import { sep } from "node:path";
import { env } from "node:process";

import {
  type AttachmentOptions,
  type ContentType,
  type Label,
  type Parameter,
  type StatusDetails,
} from "allure-js-commons";
import { Stage, Status } from "allure-js-commons";
import type { Category, RuntimeMessage } from "allure-js-commons/sdk";
import { getMessageAndTraceFromError, getStatusFromError } from "allure-js-commons/sdk";
import {
  getFallbackTestCaseIdLabel,
  getHostLabel,
  getProjectName,
  getRelativePath,
  getThreadLabel,
} from "allure-js-commons/sdk/reporter";
import {
  ReporterRuntime,
  createDefaultWriter,
  ensureSuiteLabels,
  getEnvironmentLabels,
  getFrameworkLabel,
  getLanguageLabel,
  getPackageLabel,
} from "allure-js-commons/sdk/reporter";
import { setGlobalTestRuntime } from "allure-js-commons/sdk/runtime";
import * as Mocha from "mocha";

import { doneAll, enableExtraReporters } from "./extraReporters.js";
import { setLegacyApiRuntime } from "./legacyUtils.js";
import { MochaTestRuntime } from "./MochaTestRuntime.js";
import type { AllureMochaReporterConfig, TestPlanIndices } from "./types.js";
import {
  applyTestPlan,
  createTestPlanIndices,
  getAllureDisplayName,
  getAllureFullName,
  getAllureMetaLabels,
  getAllureMetaLinks,
  getHookType,
  getLegacyTestCaseId,
  getSuitesOfMochaTest,
  getTestCaseId,
  getTestScope,
  isIncludedInTestRun,
  resolveParallelModeSetupFile,
  setTestScope,
} from "./utils.js";

const {
  EVENT_SUITE_BEGIN,
  EVENT_SUITE_END,
  EVENT_TEST_BEGIN,
  EVENT_TEST_END,
  EVENT_TEST_PASS,
  EVENT_TEST_FAIL,
  EVENT_TEST_PENDING,
  EVENT_HOOK_BEGIN,
  EVENT_HOOK_END,
  EVENT_TEST_RETRY,
} = Mocha.Runner.constants;

export class AllureMochaReporter extends Mocha.reporters.Base {
  protected readonly runtime: ReporterRuntime;
  protected readonly testplan?: TestPlanIndices;
  protected readonly testsMap: Map<string, Mocha.Test> = new Map();
  protected scopesStack: string[] = [];
  protected currentTest?: string;
  protected currentHook?: string;
  protected currentHookName?: string;
  protected currentHookError?: Error;
  private readonly isInWorker: boolean;
  readonly #extraReporters: Mocha.reporters.Base[] = [];

  constructor(runner: Mocha.Runner, opts: Mocha.MochaOptions, isInWorker: boolean = false) {
    super(runner, opts);

    const allureConfig: AllureMochaReporterConfig = opts.reporterOptions ?? {};
    const { resultsDir, extraReporters, ...restOptions } = allureConfig;

    this.isInWorker = isInWorker;
    this.runtime = new ReporterRuntime({
      writer: createDefaultWriter({ resultsDir }),
      ...restOptions,
    });
    this.testplan = createTestPlanIndices();

    const testRuntime = new MochaTestRuntime(this.applyRuntimeMessages);

    setGlobalTestRuntime(testRuntime);
    setLegacyApiRuntime(this);

    if (opts.parallel) {
      opts.require = [...(opts.require ?? []), resolveParallelModeSetupFile()];
    } else {
      this.applyListeners();
    }

    if (!isInWorker && extraReporters) {
      this.#extraReporters = enableExtraReporters(runner, opts, extraReporters);
    }
  }

  applyRuntimeMessages = (...message: RuntimeMessage[]) => {
    const root = this.currentHook ?? this.currentTest;
    if (root) {
      this.runtime.applyRuntimeMessages(root, message);
    } else {
      this.runtime.applyGlobalRuntimeMessages(message);
    }
  };

  /**
   * @deprecated for removal. Use reporter config option instead.
   */
  writeCategoriesDefinitions = (categories: Category[]) => {
    this.runtime.categories = categories;
    if (this.isInWorker) {
      // done is not called in a worker; emit the file immediately
      this.runtime.writeCategoriesDefinitions();
    }
  };

  /**
   * @deprecated for removal. Use reporter config option instead.
   */
  writeEnvironmentInfo = (environmentInfo: Record<string, string>) => {
    this.runtime.environmentInfo = environmentInfo;
    if (this.isInWorker) {
      // done is not called in a worker; emit the file immediately
      this.runtime.writeEnvironmentInfo();
    }
  };

  /**
   * @deprecated for removal. Use reporter config option instead.
   */
  testAttachment = (name: string, content: Buffer | string, options: ContentType | string | AttachmentOptions) => {
    const root = this.currentHook ?? this.currentTest;
    if (!root) {
      return;
    }
    const opts = typeof options === "string" ? { contentType: options } : options;
    const encoding = opts.encoding ?? "utf8";
    const buffer = typeof content === "string" ? Buffer.from(content, encoding) : content;
    this.runtime.writeAttachment(root, null, name, buffer, { ...opts, wrapInStep: false });
  };

  override done(failures: number, fn?: ((failures: number) => void) | undefined) {
    this.runtime.writeEnvironmentInfo();
    this.runtime.writeCategoriesDefinitions();
    doneAll(this.#extraReporters, failures, fn);
  }

  private applyListeners = () => {
    this.runner
      .on(EVENT_SUITE_BEGIN, this.onSuite)
      .on(EVENT_SUITE_END, this.onSuiteEnd)
      .on(EVENT_TEST_BEGIN, this.onTest)
      .on(EVENT_TEST_PASS, this.onPassed)
      .on(EVENT_TEST_FAIL, this.onFailed)
      .on(EVENT_TEST_RETRY, this.onFailed)
      .on(EVENT_TEST_PENDING, this.onPending)
      .on(EVENT_TEST_END, this.onTestEnd)
      .on(EVENT_HOOK_BEGIN, this.onHookStart)
      .on(EVENT_HOOK_END, this.onHookEnd);
  };

  private onSuite = (suite: Mocha.Suite) => {
    if (!suite.parent && this.testplan) {
      applyTestPlan(this.testplan.idIndex, this.testplan.fullNameIndex, suite);
    }
    const scopeUuid = this.runtime.startScope();
    this.scopesStack.push(scopeUuid);
  };

  private onSuiteEnd = (suite: Mocha.Suite) => {
    this.writeTestScopes(suite);
    const scopeUuid = this.scopesStack.pop();
    if (scopeUuid) {
      this.runtime.writeScope(scopeUuid);
    }
  };

  protected onTest = (test: Mocha.Test) => {
    if (this.currentTest) {
      const retriedTest = this.testsMap.get(this.currentTest);
      if (retriedTest) {
        this.onTestEnd(retriedTest);
        const testScope = getTestScope(retriedTest);
        if (testScope) {
          this.runtime.writeScope(testScope);
        }
      }
      this.testsMap.delete(this.currentTest);
      this.currentTest = undefined;
    }

    const globalLabels = getEnvironmentLabels().filter((label) => !!label.value);
    const initialLabels: Label[] = [
      getLanguageLabel(),
      getFrameworkLabel(this.getFrameworkName()),
      getHostLabel(),
      getThreadLabel(this.getWorkerId()),
    ];
    const metaLabels = getAllureMetaLabels(test);
    const links = getAllureMetaLinks(test);
    const labels = globalLabels.concat(initialLabels, metaLabels);

    if (test.file) {
      const packageLabel: Label = getPackageLabel(test.file);

      labels.push(packageLabel);
    }

    labels.push(getFallbackTestCaseIdLabel(getLegacyTestCaseId(test)));

    const scopeUuid = this.runtime.startScope();

    setTestScope(test, scopeUuid);

    // @ts-ignore
    const retryNum = "currentRetry" in test ? test.currentRetry() : 0;
    const parameters: Parameter[] = retryNum ? [{ name: "Retry", value: `${retryNum}`, excluded: true }] : [];

    this.currentTest = this.runtime.startTest(
      {
        name: getAllureDisplayName(test),
        stage: Stage.RUNNING,
        fullName: getAllureFullName(test),
        labels,
        links: [...links],
        testCaseId: getTestCaseId(test),
        parameters: parameters,
      },
      [...this.scopesStack, scopeUuid],
    );
    this.testsMap.set(this.currentTest, test);
  };

  private onPassed = () => {
    if (!this.currentTest) {
      return;
    }
    this.runtime.updateTest(this.currentTest, (r) => {
      r.status = Status.PASSED;
    });
  };

  private onFailed = (_: Mocha.Test, error: Error) => {
    if (this.currentHook && !this.currentHookError) {
      const details = getMessageAndTraceFromError(error);

      this.currentHookError = error;
      this.runtime.updateFixture(this.currentHook, (r) => {
        r.status = getStatusFromError(error);
        r.statusDetails = {
          ...r.statusDetails,
          ...details,
        };
      });
      this.runtime.applyGlobalRuntimeMessages([toGlobalErrorMessage(this.currentHookName ?? "hook", details)]);
    }

    if (!this.currentTest) {
      return;
    }
    this.runtime.updateTest(this.currentTest, (r) => {
      r.status = getStatusFromError(error);
      r.statusDetails = {
        ...r.statusDetails,
        ...getMessageAndTraceFromError(error),
      };
    });
  };

  private onPending = (test: Mocha.Test) => {
    if (isIncludedInTestRun(test)) {
      if (!this.currentTest) {
        this.onTest(test);
      }
      this.runtime.updateTest(this.currentTest!, (r) => {
        r.status = Status.SKIPPED;
        r.statusDetails = {
          message: "Test skipped",
        };
      });
    }
  };

  protected onTestEnd = (test: Mocha.Test) => {
    if (!this.currentTest) {
      return;
    }
    if (isIncludedInTestRun(test)) {
      const defaultSuites = getSuitesOfMochaTest(test);

      this.runtime.updateTest(this.currentTest, (t) => {
        const projectName = getProjectName();
        const fsPath: string[] = getRelativePath(test.file!).split(sep).filter(Boolean);
        ensureSuiteLabels(t, defaultSuites);

        const titlePath = fsPath.concat(...defaultSuites);
        t.titlePath = projectName ? [projectName, ...titlePath] : titlePath;
        t.stage = Stage.FINISHED;
      });
      this.runtime.stopTest(this.currentTest);
      this.runtime.writeTest(this.currentTest);
      this.testsMap.delete(this.currentTest);
      this.currentTest = undefined;

      // We're writing the test's dedicated scope in onSuiteEnd instead of here
      // because there might be afterEach hooks, which are reported after
      // onTestEnd, not before.
    }
  };

  private onHookStart = (hook: Mocha.Hook) => {
    const [hookCategory, hookScope] = getHookType(hook);
    const test = hook.ctx?.currentTest;
    const scopeUuid = hookScope === "each" && test ? getTestScope(test) : this.getCurrentSuiteScope();
    if (!scopeUuid) {
      return;
    }

    const name = hook.originalTitle ?? hook.title ?? "";
    if (hookCategory) {
      this.currentHookError = undefined;
      this.currentHook = this.runtime.startFixture(scopeUuid, hookCategory, { name });
      this.currentHookName = name;
    }
  };

  private onHookEnd = (hook: Mocha.Hook) => {
    if (!this.currentHook) {
      return;
    }
    const name = this.currentHookName ?? hook.originalTitle ?? hook.title ?? "";
    const error: Error | undefined = this.currentHookError ?? hook.error();
    this.runtime.updateFixture(this.currentHook, (r) => {
      if (error) {
        r.status = getStatusFromError(error);
        r.statusDetails = {
          ...r.statusDetails,
          ...getMessageAndTraceFromError(error),
        };
      } else {
        r.status = Status.PASSED;
      }
    });
    if (error && !this.currentHookError) {
      this.runtime.applyGlobalRuntimeMessages([toGlobalErrorMessage(name, getMessageAndTraceFromError(error))]);
    }
    this.runtime.stopFixture(this.currentHook);
    this.currentHook = undefined;
    this.currentHookName = undefined;
    this.currentHookError = undefined;
  };

  private writeTestScopes = (suite: Mocha.Suite) => {
    suite.tests.forEach((test) => {
      const testScopeUuid = getTestScope(test);
      if (testScopeUuid) {
        this.runtime.writeScope(testScopeUuid);
      }
    });
  };

  private getCurrentSuiteScope = () =>
    this.scopesStack.length > 0 ? this.scopesStack[this.scopesStack.length - 1] : undefined;

  protected getFrameworkName = (): string => "mocha";

  protected getWorkerId = (): string | undefined => env.MOCHA_WORKER_ID;
}

const toGlobalErrorMessage = (name: string, details: StatusDetails): RuntimeMessage => ({
  type: "global_error",
  data: {
    ...details,
    message: details.message ? `${name} failed: ${details.message}` : `${name} failed`,
  },
});
